import { Tooltip, Skeleton } from "antd";
import NairaSign from "Components/NairaSign";
import { useGetPendingOrdersStatsQuery } from "hooks/Dashboard/useGetPendingOrdersStatsQuery";
import { useGetTotalCreditsQuery } from "hooks/Dashboard/useGetTotalCreditsQuery";
import { useGetTotalSalesQuery } from "hooks/Dashboard/useGetTotalSalesQuery";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useGetPendingCreditsQuery } from "hooks/Dashboard/useGetPendingCreditsQuery";
import KoboConverter from "Components/KoboConverter";
import { useGetTotalCashAtHandQuery } from "hooks/Dashboard/useGetTotalCashAtHandQuery";
import { useGetTotalCreditRepaidQuery } from "hooks/Dashboard/useGetTotalCreditRepaidQuery";

const DashboardCards = ({
  timePeriod,
  customStartDate,
  customEndDate,
}: {
  timePeriod: string;
  customStartDate: string;
  customEndDate: string;
}) => {
  const { data: totatStats, isLoading: isLoadingTotalSales } =
    useGetTotalSalesQuery({
      timePeriod: timePeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate,
    });

  const { data: pendingOrder, isLoading: isLoadingPendingOrder } =
    useGetPendingOrdersStatsQuery({
      timePeriod: timePeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate,
    });

  const { data: totalCredit, isLoading: isLoadingTotalCredit } =
    useGetTotalCreditsQuery({
      timePeriod: timePeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate,
    });

  const { data: pendingCredit, isLoading: isLoadingPendingCredit } =
    useGetPendingCreditsQuery({
      timePeriod: timePeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate,
    });

  const { data: cib, isLoading: isLoadingCib } = useGetTotalCashAtHandQuery({
    timePeriod: timePeriod,
    customStartDate: customStartDate,
    customEndDate: customEndDate,
  });

  const { data: tcr, isLoading: isLoadingTcr } = useGetTotalCreditRepaidQuery({
    timePeriod: timePeriod,
    customStartDate: customStartDate,
    customEndDate: customEndDate,
  });

  return (
    <div>
      <div className="grid grid-cols-3 gap-4">
        <div className="p-[18px] rounded-[10px] bg-[#FFF5F0]">
          <p className="text-grey">Total Sales</p>
          <h3 className="mt-[25px] text-black text-[18px] font-bold">
            {isLoadingTotalSales ? (
              <Skeleton.Input active size="large" />
            ) : (
              <>
                <KoboConverter
                  amount={
                    totatStats?.data?.totalSalesAmount
                      ? totatStats?.data?.totalSalesAmount
                      : 0
                  }
                />
              </>
            )}
          </h3>
          <div className="flex items-center justify-between">
            <span>
              {isLoadingTotalSales ? (
                <Skeleton.Input active size="small" />
              ) : totatStats?.data?.percentageChange ? (
                totatStats?.data?.percentageChange
              ) : (
                0 + "%"
              )}
            </span>
            <span>
              {isLoadingTotalSales ? (
                <Skeleton.Input active size="small" />
              ) : totatStats?.data?.totalOrdersCount ? (
                totatStats?.data?.totalOrdersCount
              ) : (
                "0" + " Order counts"
              )}
            </span>
          </div>
        </div>
        <div className="p-[18px] rounded-[10px] bg-[#EEEEEE]">
          <p className="text-grey">Pending Order</p>
          <h3 className="mt-[25px] text-black text-[18px] font-bold">
            {isLoadingPendingOrder ? (
              <Skeleton.Input active size="large" />
            ) : (
              <>{pendingOrder?.data?.pendingOrdersCurrentPeriod} </>
            )}
          </h3>
          <div className="flex items-center justify-between">
            <span>
              {isLoadingPendingOrder ? (
                <Skeleton.Input active size="small" />
              ) : pendingOrder?.data?.percentageChange ? (
                pendingOrder?.data?.percentageChange
              ) : (
                0 + "%"
              )}
            </span>
            <span>
              {isLoadingPendingOrder ? (
                <Skeleton.Input active size="small" />
              ) : (
                <>
                  {pendingOrder?.data?.pendingOrdersPreviousPeriod.toLocaleString()}{" "}
                  <Tooltip placement="bottom" title={"Previous Pending Order"}>
                    <button>
                      PPO <InfoCircleOutlined />
                    </button>
                  </Tooltip>
                </>
              )}
            </span>
          </div>
        </div>
        <div className="p-[18px] rounded-[10px] bg-[#EEEEEE]">
          <p className="text-grey">Total Credit Issued</p>
          <h3 className="mt-[25px] text-black text-[18px] font-bold">
            {isLoadingTotalCredit ? (
              <Skeleton.Input active size="large" />
            ) : (
              <>
                <KoboConverter
                  amount={
                    totalCredit?.data?.totalCreditsCurrentPeriod
                      ? totalCredit?.data?.totalCreditsCurrentPeriod
                      : 0
                  }
                />
              </>
            )}
          </h3>
          <div className="flex items-center justify-between">
            <span>
              {isLoadingTotalCredit ? (
                <Skeleton.Input active size="small" />
              ) : totalCredit?.data?.percentageChange ? (
                totalCredit?.data?.percentageChange
              ) : (
                +"%"
              )}
            </span>
            <span>
              {isLoadingTotalCredit ? (
                <Skeleton.Input active size="small" />
              ) : (
                <>
                  {totalCredit?.data?.totalWalletsCurrentPeriod}
                  <Tooltip
                    placement="bottom"
                    title={"Total Wallets Current Period"}
                  >
                    <button>
                      TWCP <InfoCircleOutlined />
                    </button>
                  </Tooltip>
                </>
              )}
            </span>
          </div>
        </div>
        <div className="p-[18px] rounded-[10px] bg-[#EEEEEE]">
          <p className="text-grey">Pending Credit Application</p>
          <h3 className="mt-[25px] text-black text-[18px] font-bold">
            {isLoadingPendingCredit ? (
              <Skeleton.Input active size="large" />
            ) : (
              pendingCredit?.data?.totalPendingApplicationsCurrentPeriod
            )}
          </h3>
          <div className="flex items-center justify-between">
            <span>
              {isLoadingPendingCredit ? (
                <Skeleton.Input active size="small" />
              ) : pendingCredit?.data?.percentageChange ? (
                pendingCredit?.data?.percentageChange
              ) : (
                0 + "%"
              )}
            </span>
            <span>
              {isLoadingPendingCredit ? (
                <Skeleton.Input active size="small" />
              ) : (
                <>
                  {pendingCredit?.data?.totalPendingApplicationsPreviousPeriod}{" "}
                  <Tooltip
                    placement="bottom"
                    title={"Total Pending Applications Previous Period"}
                  >
                    <button>
                      TPAPP <InfoCircleOutlined />
                    </button>
                  </Tooltip>
                </>
              )}{" "}
            </span>
          </div>
        </div>
        <div className="p-[18px] rounded-[10px] bg-[#EEEEEE]">
          <p className="text-grey">Sales (CIB)</p>

          <div className="flex items-center justify-between mt-[25px]">
            <h3 className="text-black text-[18px] font-bold">
              {isLoadingCib ? (
                <Skeleton.Input active size="large" />
              ) : (
                <KoboConverter
                  amount={
                    cib?.data?.totalCashAtHandAmount
                      ? cib?.data?.totalCashAtHandAmount
                      : 0
                  }
                />
              )}
            </h3>
            {isLoadingPendingCredit ? (
              <Skeleton.Input active size="small" />
            ) : (
              <>
                {cib?.data?.isLoadingCib}{" "}
                <Tooltip placement="bottom" title={"Sales - Excluding wallet"}>
                  <button>
                    CIB <InfoCircleOutlined />
                  </button>
                </Tooltip>
              </>
            )}
          </div>
        </div>
        <div className="p-[18px] rounded-[10px] bg-[#EEEEEE]">
          <p className="text-grey">Total Credit Repaid</p>
          <h3 className="mt-[25px] text-black text-[18px] font-bold">
            {isLoadingTcr ? (
              <Skeleton.Input active size="large" />
            ) : (
              <KoboConverter
                amount={
                  tcr?.data?.totalCreditRepaidAmount
                    ? tcr?.data?.totalCreditRepaidAmount
                    : 0
                }
              />
            )}
          </h3>
          <div className="flex items-center justify-between">
            <span>
              {/* {isLoadingTcr ? (
                <Skeleton.Input active size="small" />
              ) : (
                tcr?.data?.percentageChange + "%"
              )} */}
            </span>
            {/* <span>
              {isLoadingPendingCredit ? (
                <Skeleton.Input active size="small" />
              ) : (
                <>
                  {tcr?.data?.totalCreditRepaidAmount}{" "}
                  <Tooltip
                    placement="bottom"
                    title={"Total Pending Applications Previous Period"}
                  >
                    <button>
                      TPAPP <InfoCircleOutlined />
                    </button>
                  </Tooltip>
                </>
              )}
            </span> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCards;
